// Real partners, collaborators, funders & ecosystem relationships.
// Source of truth — used across the site (Impact, Partnerships, Home,
// Corporate, programme pages, Footer). Typography-led cards by default;
// each partner can optionally specify a logoUrl that the admin uploads later.
//
// To add a new partner without a developer:
//   1. Upload the logo via Admin → Events drawer (uses /api/admin/uploads),
//      copy the resulting URL.
//   2. Send the team the name, role line, tier ("flagship" | "supporter"
//      | "ecosystem"), category ("funder" | "venue" | "council" |
//      "youth" | "creative" | "wellbeing" | "academic"), and logo URL.
//   3. A developer will add the entry to this file (P2 — admin UI for
//      partners management).

export const PARTNER_CATEGORIES = {
  funder: { label: "Funder", emoji: "Funder" },
  council: { label: "Council & local authority", emoji: "Council" },
  venue: { label: "Venue & studio", emoji: "Venue" },
  youth: { label: "Youth & community", emoji: "Youth" },
  creative: { label: "Creative collaborator", emoji: "Creative" },
  wellbeing: { label: "Wellbeing & NHS", emoji: "Wellbeing" },
  academic: { label: "Academic", emoji: "Academic" },
  cultural: { label: "Cultural institution", emoji: "Cultural" },
};

export const PARTNER_TIERS = {
  flagship: "Flagship partner",
  supporter: "Funded / supported by",
  ecosystem: "Ecosystem collaborator",
};

/**
 * @typedef {Object} Partner
 * @property {string} id
 * @property {string} name
 * @property {keyof PARTNER_CATEGORIES} category
 * @property {keyof PARTNER_TIERS} tier
 * @property {string} role         — short one-line description of the relationship
 * @property {string=} websiteUrl
 * @property {string=} logoUrl     — optional. If absent, a typography card is rendered.
 * @property {string[]} programmes — which TOK programmes / pages they relate to (used to filter)
 */
export const TOK_PARTNERS = [
  // ---------- Flagship partners ----------
  {
    id: "hypha-studios",
    name: "Hypha Studios",
    category: "venue",
    tier: "flagship",
    role: "Home venue, Chatham — large-scale immersive production space, projection rig and creative residencies.",
    websiteUrl: "https://www.hyphastudios.com",
    programmes: ["experiences", "level-up", "ubuntu", "festivals", "home-education"],
  },
  {
    id: "docking-station",
    name: "Docking Station",
    category: "venue",
    tier: "flagship",
    role: "Creative space partner — co-presenting community gatherings, exhibitions and pop-up immersive nights across Medway.",
    programmes: ["experiences", "community", "festivals"],
  },

  // ---------- Funders ----------
  {
    id: "arts-council-england",
    name: "Arts Council England",
    category: "funder",
    tier: "supporter",
    role: "Investment Principles aligned. Funded creative practice, talent development and access programmes.",
    websiteUrl: "https://www.artscouncil.org.uk",
    programmes: ["impact", "level-up", "festivals", "community"],
  },
  {
    id: "national-lottery",
    name: "The National Lottery",
    category: "funder",
    tier: "supporter",
    role: "Made possible with funds from The National Lottery — supporting global majority storytelling, youth pathways and immersive heritage.",
    websiteUrl: "https://www.tnlcommunityfund.org.uk",
    programmes: ["impact", "community", "home-education", "ubuntu"],
  },

  // ---------- Council & local authority ----------
  {
    id: "medway-council",
    name: "Medway Council",
    category: "council",
    tier: "ecosystem",
    role: "Cultural strategy, schools outreach and the Medway-wide programming partnership.",
    websiteUrl: "https://www.medway.gov.uk",
    programmes: ["impact", "home-education", "community", "festivals", "corporate", "level-up"],
  },
  {
    id: "kent-county-council",
    name: "Kent County Council",
    category: "council",
    tier: "ecosystem",
    role: "Kent-wide cultural access, alternative provision and youth pathway partnerships.",
    websiteUrl: "https://www.kent.gov.uk",
    programmes: ["impact", "home-education", "community", "corporate", "level-up"],
  },

  // ---------- Youth & community ----------
  {
    id: "medway-youth",
    name: "Medway Youth",
    category: "youth",
    tier: "ecosystem",
    role: "Youth voice partner — referrals, co-production and progression routes for 16–25s into LEVEL UP.",
    programmes: ["impact", "level-up", "community", "home-education"],
  },

  // ---------- Community collaborators ----------
  {
    id: "global-majority-creators",
    name: "Global Majority Creators Network",
    category: "creative",
    tier: "ecosystem",
    role: "Network of African, Caribbean and global majority artists, technologists and producers shaping TOK commissions.",
    programmes: ["community", "festivals", "level-up"],
  },
];

// ---- Filters / selectors used by UI components -----------------------------

export function getPartnersForProgramme(programmeKey) {
  return TOK_PARTNERS.filter((p) => p.programmes.includes(programmeKey));
}

export function getPartnersByTier(tier) {
  return TOK_PARTNERS.filter((p) => p.tier === tier);
}

export function getFlagshipPartners() {
  return TOK_PARTNERS.filter((p) => p.tier === "flagship");
}

export function getFunders() {
  return TOK_PARTNERS.filter((p) => p.category === "funder");
}

export function getCouncils() {
  return TOK_PARTNERS.filter((p) => p.category === "council");
}

export function getYouthAndCommunity() {
  return TOK_PARTNERS.filter(
    (p) => p.category === "youth" || p.category === "creative",
  );
}

/**
 * Curated list shown in the page-wide trust band & footer.
 * Explicit so editorial control sits with the people who own the partnerships
 * narrative, not the data shape.
 */
export const TRUST_BAND_PARTNER_IDS = [
  "hypha-studios",
  "docking-station",
  "arts-council-england",
  "national-lottery",
  "medway-council",
  "kent-county-council",
  "medway-youth",
];

export function getTrustBandPartners() {
  const index = Object.fromEntries(TOK_PARTNERS.map((p) => [p.id, p]));
  return TRUST_BAND_PARTNER_IDS.map((id) => index[id]).filter(Boolean);
}
